import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function PricingSection({ id }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [currentPlan, setCurrentPlan] = useState('free');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
    fetchCurrentPlan();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/billing/plans');
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentPlan = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await api.get('/auth/me');
        setCurrentPlan(response.data.user.plan || 'free');
      }
    } catch (error) {
      console.log('Not logged in or error fetching user plan:', error);
      setCurrentPlan('free');
    }
  };

  const getButtonText = (planId) => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const isLoggedIn = !!token;

    // Show loading state
    if (checkoutLoading === planId) {
      return 'Loading...';
    }

    // NOT LOGGED IN - Show "Get Started" for all plans
    if (!isLoggedIn) {
      return 'Get Started';
    }

    // LOGGED IN - Check current plan status
    if (currentPlan === planId) {
      return 'Current Plan';
    }

    // Free plan downgrade
    if (planId === 'free' && currentPlan !== 'free') {
      return 'Downgrade';
    }

    // Upgrading from free to paid
    if (currentPlan === 'free' && planId !== 'free') {
      return 'Upgrade';
    }

    // Switching between paid plans
    if (currentPlan !== 'free' && planId !== 'free') {
      return 'Switch Plan';
    }

    // Fallback
    return 'Get Started';
  };

  const isButtonDisabled = (planId) => {
    // Current plan is disabled
    if (currentPlan === planId) {
      return true;
    }
    // Disable all buttons while processing
    if (checkoutLoading !== null) {
      return true;
    }
    return false;
  };

  const handleSelectPlan = async (planId) => {
    // Don't allow clicking current plan
    if (currentPlan === planId) {
      return;
    }

    // Check if user is logged in
    const token = localStorage.getItem('token');

    // Handle Free plan
    if (planId === 'free') {
      if (!token) {
        // Not logged in - take to register
        navigate('/register');
        return;
      }

      if (currentPlan !== 'free') {
        // Show confirmation modal for downgrade
        const plan = plans.find(p => p.id === planId);
        setSelectedPlan(plan);
        setShowConfirmModal(true);
      }
      return;
    }

    // Handle Enterprise plan (if it exists)
    if (planId === 'enterprise' || planId === 'scale') {
      window.location.href = 'mailto:sales@snaptura.online?subject=Enterprise Plan Inquiry';
      return;
    }

    // Must be logged in for paid plans
    if (!token) {
      navigate('/register');
      return;
    }

    // Show confirmation modal for plan changes
    const plan = plans.find(p => p.id === planId);
    setSelectedPlan(plan);
    setShowConfirmModal(true);
  };

  const confirmPlanChange = async () => {
    if (!selectedPlan) return;

    const planId = selectedPlan.id;
    setShowConfirmModal(false);

    // Handle downgrade to free
    if (planId === 'free') {
      try {
        setCheckoutLoading(planId);
        await api.post('/billing/cancel-subscription');
        toast.success('Your subscription will be canceled at the end of the billing period. You\'ll be downgraded to the Free plan.');

        // Refresh current plan info
        const userResponse = await api.get('/auth/me');
        setCurrentPlan(userResponse.data.user.plan || 'free');
      } catch (error) {
        console.error('Cancel error:', error);
        toast.error('Failed to cancel subscription. Please try again.');
      } finally {
        setCheckoutLoading(null);
      }
      return;
    }

    // Handle paid plan subscription/upgrade
    try {
      setCheckoutLoading(planId);
      const response = await api.post('/billing/create-checkout-session', {
        plan: planId
      });

      // Check if it was an instant upgrade (no redirect URL)
      if (response.data.success) {
        // Instant upgrade happened!
        const invoice = response.data.invoice;
        if (invoice) {
          toast.success(
            `Successfully changed to ${selectedPlan.name} plan! ${invoice.amountDue > 0 ? `You'll be charged $${invoice.amountDue.toFixed(2)} (prorated).` : 'Prorated credit applied to your account.'}`
          );
        } else {
          toast.success(`Successfully changed to ${selectedPlan.name} plan!`);
        }

        // Refresh current plan
        const userResponse = await api.get('/auth/me');
        setCurrentPlan(userResponse.data.user.plan || 'free');
      } else if (response.data.url) {
        // New subscription - redirect to Stripe checkout
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center">
        <div className="text-gray-500">Loading pricing...</div>
      </div>
    );
  }

  return (
    <section id={id} className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600">
            Choose the perfect plan for your needs
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8 max-w-7xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 ${
                plan.highlighted ? 'ring-2 ring-blue-500' : ''
              } ${currentPlan === plan.id ? 'ring-2 ring-green-500' : ''}`}
            >
              {plan.highlighted && currentPlan !== plan.id && (
                <div className="bg-blue-600 text-white text-center py-2 text-sm font-semibold">
                  Most Popular
                </div>
              )}

              {currentPlan === plan.id && (
                <div className="bg-green-600 text-white text-center py-2 text-sm font-semibold">
                  Your Current Plan
                </div>
              )}

              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {plan.name}
                </h3>
                <p className="text-gray-600 text-sm mb-6">{plan.description}</p>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">
                    ${plan.price}
                  </span>
                  <span className="text-gray-600">/month</span>
                </div>

                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={isButtonDisabled(plan.id)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                    currentPlan === plan.id
                      ? 'bg-green-100 text-green-700 cursor-not-allowed'
                      : plan.highlighted
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  } ${isButtonDisabled(plan.id) && currentPlan !== plan.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {getButtonText(plan.id)}
                </button>

                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg
                        className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Enterprise CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-8 max-w-2xl mx-auto border border-indigo-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Need more than 50,000 requests?
            </h3>
            <p className="text-gray-600 mb-4">
              Contact us for custom enterprise pricing, volume discounts, and dedicated support
            </p>
            <a
              href="mailto:sales@snaptura.online?subject=Enterprise Plan Inquiry"
              className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Contact Sales
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {selectedPlan.id === 'free' ? 'Downgrade to Free Plan?' : `Switch to ${selectedPlan.name} Plan?`}
            </h3>

            {/* Plan Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">New Plan</span>
                <span className="text-lg font-bold text-indigo-600">{selectedPlan.name}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Monthly Cost</span>
                <span className="text-lg font-bold text-gray-900">${selectedPlan.price}/month</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Monthly Quota</span>
                <span className="text-sm text-gray-900">{selectedPlan.requestsPerMonth.toLocaleString()} requests</span>
              </div>
            </div>

            {/* Warning or Info Message */}
            {selectedPlan.id === 'free' ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Important:</strong> Your subscription will be canceled at the end of the current billing period.
                  You'll then be downgraded to the Free plan with 100 requests/month.
                </p>
              </div>
            ) : currentPlan !== 'free' ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Your subscription will be updated immediately.
                  Stripe will automatically prorate the charges - you'll only pay for what you use.
                  {' '}The exact amount will be shown on your next invoice.
                </p>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800">
                  <strong>Welcome!</strong> You'll be redirected to Stripe's secure checkout to complete your subscription.
                </p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={confirmPlanChange}
                disabled={checkoutLoading !== null}
                className="flex-1 bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
              >
                {checkoutLoading !== null ? 'Processing...' : selectedPlan.id === 'free' ? 'Yes, Downgrade' : 'Continue'}
              </button>
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={checkoutLoading !== null}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}